import axios from 'axios';
import { CollectionWithCursor, FavoritesData } from '@inplayer-org/inplayer.js';
import { Auth } from 'aws-amplify';

import { useConfigStore } from '../../stores/ConfigStore';
import { FormValidationError } from '../../errors/FormValidationError';
import type { RequestOptions } from '../../../types/account';
import { useAccountStore } from '../../stores/AccountStore';
import { useAppConfig } from '../../../../hooks-react/src/useAppConfig';
import { useAppInfo } from '../../../../hooks-react/src/useAppInfo';
import { getApolloClient, getAuthApolloClient } from '../../graphql/ApolloClient';
import {
  GET_CONTINUE_WATCHING_LIST,
  GET_CONTINUE_WATCHING_BY_JW_MEDIA_ID,
  GetContinueWatchingByJWMediaIdData,
  GetContinueWatchingListData,
} from '../../graphql/query/continueWatching.queries';
import { INSERT_CONTINUE_WATCHING, REMOVE_CONTINUE_WATCHING } from '../../graphql/mutation/continueWatching.mutations';
import {
  SIGN_IN_MUTATION,
  SignInData,
  SIGN_UP_MUTATION,
  SignUpData,
  DELETE_ACCOUNT_MUTATION,
  DeleteAccountData,
  CHANGE_PASSWORD_MUTATION,
  ChangePasswordData,
  RESET_PASSWORD_MUTATION,
  ResetPasswordData,
  VALIDATE_OTP_MUTATION,
  ValidateOtpData,
} from '../../graphql/mutation/auth.mutations';
import {
  GET_CURRENT_USER_QUERY,
  CurrentUserData,
  SIGN_OUT_QUERY,
  SignOutData,
  GET_ACCOUNT_EXISTENCE_QUERY,
  AccountExistenceData,
  SEND_USER_RECOVERY_MAIL_QUERY,
  SendUserRecoveryMailData,
  REQUEST_FORGET_PASSWORD_OTP_QUERY,
  RequestForgetPasswordOtpData,
} from '../../graphql/query/auth.queries';
import { GetCookie } from '../../utils/cookie';
import { clearAuthStorage } from '../../utils/auth';

const getAccessToken = (): string | null => {
  return GetCookie('AccessToken');
};

const getJWTToken = () => {
  const token = GetCookie('InplayerToken');
  if (token) {
    const parsedToken = typeof token === 'string' ? JSON.parse(token) : token;
    if (parsedToken?.token) {
      return parsedToken.token;
    }
  }
};

const getHeaders = (token?: string) => ({
  'Content-Type': 'application/json',
  Authorization: token ? `Bearer ${token}` : '',
});

const getBaseURL = (): string => {
  const { appConfig } = useConfigStore.getState();
  const { customBaseApiUrl } = useAppInfo();
  return customBaseApiUrl || appConfig?.api?.base_url || '';
};

const getEndpoint = (path: string | undefined): string | null => {
  const baseURL = getBaseURL();
  return baseURL && path ? `${baseURL}${path}` : null;
};

export const getUserProfile = async (): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.get_user_profile);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.get(endpoint, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

export const createUserProfile = async (payload: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.create_user_profile);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.post(endpoint, payload, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error creating user profile:', error);
    return null;
  }
};

export const getCustomFavorites = async (): Promise<CollectionWithCursor<FavoritesData>> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return { collection: [], cursor: '' };
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.favorite?.get_favorite);
  if (!endpoint) {
    console.error('API config not ready');
    return { collection: [], cursor: '' };
  }

  try {
    const response = await axios.get(endpoint, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error fetching favorites:', error);
    return { collection: [], cursor: '' };
  }
};

export const updateCustomFavorites = async (mediaId: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.favorite?.update_favorite);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const payload = new URLSearchParams();
  payload.append('media_id', mediaId);

  try {
    const response = await axios.post(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating favorite:', error);
    return null;
  }
};

export const deleteCustomFavorites = async (mediaId: string): Promise<any | null> => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpointBase = getEndpoint(appConfig?.api?.end_point?.favorite?.remove_favorite);
  if (!endpointBase) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(`${endpointBase}/${mediaId}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting favorite media:', error);
    return null;
  }
};

export const getCustomWatchHistoryItem = async (mediaId: string): Promise<any> => {
  const { isCognitoEnabled, isKatonCognitoEnabled, isContinueWatchingEnabled } = useAppConfig();
  if (isCognitoEnabled || isKatonCognitoEnabled) {
    try {
      if (!isContinueWatchingEnabled) {
        return null;
      }
      const { data } = await getApolloClient().query<GetContinueWatchingByJWMediaIdData>({
        query: GET_CONTINUE_WATCHING_BY_JW_MEDIA_ID,
        variables: { jwMediaId: mediaId },
        fetchPolicy: 'network-only',
      });
      const node = data?.getContinueWatchingByJWmediaid;
      if (!node) return null;
      return {
        media_id: node.cwJwMediaId,
        progress: node.cwWatchedPosition,
        created_at: new Date(node.cwCreatedAt).getTime() / 1000,
        updated_at: new Date(node.cwUpdatedAt).getTime() / 1000,
      };
    } catch (error) {
      console.error('Error fetching watch history item (GraphQL):', error);
      return null;
    }
  }
  return null;
};

export const getCustomWatchHistory = async (offset: number = 0, first: number = 500): Promise<any> => {
  const { isCognitoEnabled, isKatonCognitoEnabled, isContinueWatchingEnabled } = useAppConfig();
  if (isCognitoEnabled || isKatonCognitoEnabled) {
    if (!isContinueWatchingEnabled) {
      return { collection: [], cursor: '', totalCount: 0 };
    }
    try {
      const { data } = await getApolloClient().query<GetContinueWatchingListData>({
        query: GET_CONTINUE_WATCHING_LIST,
        variables: { offset, first },
        fetchPolicy: 'network-only',
      });
      return {
        collection: data?.getContinueWatchingList.nodes.map((node) => ({
          media_id: node.cwJwMediaId,
          progress: node.cwWatchedPosition,
          created_at: new Date(node.cwCreatedAt).getTime() / 1000,
          updated_at: new Date(node.cwUpdatedAt).getTime() / 1000,
        })),
        totalCount: data?.getContinueWatchingList.totalCount,
        cursor: '',
      };
    } catch (error) {
      console.error('Error fetching watch history (GraphQL):', error);
      return { collection: [], cursor: '', totalCount: 0 };
    }
  }

  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.watch_history?.get_watch_history);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.get(endpoint, { headers: getHeaders(token) });
    return response.data;
  } catch (error) {
    console.error('Error fetching watch history:', error);
    return null;
  }
};

export const updateCustomWatchHistory = async (mediaId: string, progress: number): Promise<any | null> => {
  const { isCognitoEnabled, isKatonCognitoEnabled, isContinueWatchingEnabled } = useAppConfig();
  if (isCognitoEnabled || isKatonCognitoEnabled) {
    if (!isContinueWatchingEnabled) {
      return null;
    }
    try {
      const { data } = await getApolloClient().mutate({
        mutation: INSERT_CONTINUE_WATCHING,
        variables: {
          input: {
            jwMediaId: mediaId,
            watchedPosition: progress,
          },
        },
        refetchQueries: [
          {
            query: GET_CONTINUE_WATCHING_BY_JW_MEDIA_ID,
            variables: { jwMediaId: mediaId },
          },
        ],
      });
      return data;
    } catch (error) {
      console.error('Error updating watch history (GraphQL):', error);
      return null;
    }
  }

  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.watch_history?.update_watch_history);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const payload = new URLSearchParams();
  payload.append('media_id', mediaId);
  payload.append('progress', progress?.toString() || '0');

  try {
    const response = await axios.post(endpoint, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating watch history:', error);
    return null;
  }
};

export const deleteCustomWatchHistory = async (mediaId: string): Promise<any | null> => {
  const { isCognitoEnabled, isKatonCognitoEnabled, isContinueWatchingEnabled } = useAppConfig();
  if (isCognitoEnabled || isKatonCognitoEnabled) {
    if (!isContinueWatchingEnabled) {
      return null;
    }
    try {
      const { data } = await getApolloClient().mutate({
        mutation: REMOVE_CONTINUE_WATCHING,
        variables: { jwMediaId: mediaId },
        refetchQueries: [
          {
            query: GET_CONTINUE_WATCHING_BY_JW_MEDIA_ID,
            variables: { jwMediaId: mediaId },
          },
        ],
      });
      return data;
    } catch (error) {
      console.error('Error deleting watch history (GraphQL):', error);
      return null;
    }
  }

  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpointBase = getEndpoint(appConfig?.api?.end_point?.watch_history?.remove_watch_history);
  if (!endpointBase) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(`${endpointBase}/${mediaId}`, {
      headers: getHeaders(token),
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting watch history:', error);
    return null;
  }
};

export const customDeleteAccount = async (password?: string): Promise<any | null> => {
  const { isKatonCognitoEnabled } = useAppConfig();

  if (isKatonCognitoEnabled && password) {
    try {
      const { data } = await getAuthApolloClient().mutate<DeleteAccountData>({
        mutation: DELETE_ACCOUNT_MUTATION,
        variables: { password: password.trim() },
      });

      return data?.deleteAccount;
    } catch (error: any) {
      console.error('Cognito Delete Account Error:', error);
      throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Delete account failed');
    }
  }

  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }

  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.delete_account);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  try {
    const response = await axios.delete(endpoint, { headers: getHeaders(token) });
    return response;
  } catch (error) {
    console.error('Error deleting user profile:', error);
    return null;
  }
};

export const customChangePassword = async (oldPassword: string, password: string, passwordConfirmation: string) => {
  const token = getAccessToken();
  if (!token) {
    console.error('No access token found in localStorage');
    return null;
  }
  const { isCustomApiEnabled } = useAppConfig();
  const { appConfig } = useConfigStore.getState();
  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.change_password);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const body = new URLSearchParams();
  body.append('password', password);
  body.append('password_confirmation', passwordConfirmation);
  body.append('old_password', oldPassword);

  try {
    const response = await axios.post(endpoint, body.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.status === 200) {
      if (isCustomApiEnabled) {
        cognitoLogout();
      }
    }

    return response;
  } catch (error: any) {
    console.error('Error changePassword', error);
    throw error?.response?.data?.message;
  }
};

export const cognitoLogout = async () => {
  const { authentication_type } = useConfigStore.getState().featureFlags?.common || {};

  const refreshToken = GetCookie('RefreshToken');

  if (refreshToken && authentication_type === 'katon_cognito') {
    try {
      await getAuthApolloClient().query<SignOutData>({
        query: SIGN_OUT_QUERY,
        variables: { refreshToken },
      });
    } catch (error) {
      console.error('Cognito SignOut API error:', error);
    }
  }

  await Auth.signOut();
  clearAuthStorage();
  window.location.reload();
};

const performRequest = async (path: string = '/', method = 'GET', body?: string, options: RequestOptions = {}) => {
  try {
    const token = getJWTToken();

    const resp = await fetch(`${path}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      keepalive: options.keepalive,
      method,
      body,
    });

    return await resp.json();
  } catch (error: unknown) {
    return {
      errors: Array.of(error as string),
    };
  }
};

const get = <T>(path: string, options?: RequestOptions) => performRequest(path, 'GET', undefined, options) as Promise<T>;

export const LoginByCode = async (QrCode: string) => {
  const { appConfig } = useConfigStore.getState();
  useAccountStore.setState({ loading: true });

  const endpoint = getEndpoint(appConfig?.api?.end_point?.user?.activate_tv);
  if (!endpoint) {
    console.error('API config not ready');
    return null;
  }

  const response: any = await get(`${endpoint}/${QrCode}`, { authenticate: true });

  if (!response.ok) {
    console.error('API request failed:', response);
    throw new FormValidationError({ form: [response.error] });
  }
  if (response.ok) {
    useAccountStore.setState({ loading: false });
  }
  return response;
};

export const cognitoSignUp = async (input: any) => {
  try {
    const { data } = await getAuthApolloClient().mutate<SignUpData>({
      mutation: SIGN_UP_MUTATION,
      variables: {
        input,
      },
    });

    return data?.signUp;
  } catch (error: any) {
    console.error('Cognito Sign Up Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Sign up failed');
  }
};

export const cognitoSignIn = async (uUserName: string, password: string) => {
  try {
    const { data } = await getAuthApolloClient().mutate<SignInData>({
      mutation: SIGN_IN_MUTATION,
      variables: {
        input: {
          uUserName: uUserName.trim(),
          password: password.trim(),
        },
      },
    });

    return data?.signIn;
  } catch (error: any) {
    console.error('Cognito Sign In Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Login failed');
  }
};

export const getCurrentUser = async () => {
  try {
    const { data } = await getAuthApolloClient().query<CurrentUserData>({
      query: GET_CURRENT_USER_QUERY,
      fetchPolicy: 'network-only',
    });

    return data?.getUser;
  } catch (error: any) {
    console.error('Get Current User Error:', error);
    return null;
  }
};

export const checkAccountExistence = async (input: string, type: string) => {
  try {
    const { data } = await getAuthApolloClient().query<AccountExistenceData>({
      query: GET_ACCOUNT_EXISTENCE_QUERY,
      variables: { input, type },
      fetchPolicy: 'network-only',
    });

    return data?.getAccountExistence;
  } catch (error: any) {
    console.error('Check Account Existence Error:', error);
    return false;
  }
};

export const changeKatonPassword = async (oldPassword: string, newPassword: string) => {
  try {
    const { data } = await getAuthApolloClient().mutate<ChangePasswordData>({
      mutation: CHANGE_PASSWORD_MUTATION,
      variables: {
        input: {
          oldPassword: oldPassword.trim(),
          newPassword: newPassword.trim(),
        },
      },
    });

    return data?.changePassword;
  } catch (error: any) {
    console.error('Cognito Change Password Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Change password failed');
  }
};

export const requestForgetPasswordOtp = async (uEmailId: string) => {
  try {
    const response = await getAuthApolloClient().query<RequestForgetPasswordOtpData>({
      query: REQUEST_FORGET_PASSWORD_OTP_QUERY,
      variables: { uEmailId },
      fetchPolicy: 'network-only',
    });

    return response;
  } catch (error: any) {
    console.error('Request Forget Password OTP Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Request OTP failed');
  }
};

export const validateOtp = async (email: string, code: string) => {
  try {
    const response = await getAuthApolloClient().mutate<ValidateOtpData>({
      mutation: VALIDATE_OTP_MUTATION,
      variables: { email, code },
    });

    return response;
  } catch (error: any) {
    console.error('Validate OTP Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'OTP validation failed');
  }
};

export const sendUserRecoveryMail = async (uEmailId: string) => {
  try {
    const response = await getAuthApolloClient().query<SendUserRecoveryMailData>({
      query: SEND_USER_RECOVERY_MAIL_QUERY,
      variables: { uEmailId },
      fetchPolicy: 'network-only',
    });

    return response;
  } catch (error: any) {
    console.error('Send User Recovery Mail Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'User recovery failed');
  }
};

export const resetPassword = async (email: string, code: string, newPassword: string) => {
  try {
    const response = await getAuthApolloClient().mutate<ResetPasswordData>({
      mutation: RESET_PASSWORD_MUTATION,
      variables: { email, code, newPassword },
    });

    return response;
  } catch (error: any) {
    console.error('Reset Password Error:', error);
    throw new Error(error?.graphQLErrors?.[0]?.message || error?.message || 'Reset password failed');
  }
};

